// Centralized SEO config + JSON-LD schema builders for Brick Lane
// Keep target keywords + factual data in ONE place so every page stays in sync.

export const SITE_URL = 'https://bricklanela.com';

export const SITE = {
  name: 'Brick Lane',
  legalName: 'Brick Lane Los Angeles',
  tagline: 'Los Angeles Soul. Indian Roots.',
  description:
    'Brick Lane is a modern Indian restaurant in the Los Angeles Arts District. Chef-driven woodfire & tandoor cooking, regional dishes reinterpreted, and craft cocktails inspired by Indian spices and California spirit.',
  short:
    'Modern Indian restaurant in the LA Arts District. Woodfire & tandoor cooking, regional dishes reimagined, craft cocktails. Chef Sanjay Rawat.',
  url: SITE_URL,
  logo: '/api/upload/images/b57dd0389093441f8ae04dcbc70eabef.jpg',
  image: '/api/upload/images/ede34a7d951a4a64b91051cebd24ae0c.gif',
  phone: '+1-213-395-0110',
  email: 'reservations@bricklanela.com',
  priceRange: '$$$',
  address: {
    streetAddress: '1331 E 6th St',
    addressLocality: 'Los Angeles',
    addressRegion: 'CA',
    postalCode: '90021',
    addressCountry: 'US',
    neighborhood: 'Arts District',
  },
  geo: { latitude: 34.0378, longitude: -118.2305 },
  servesCuisine: ['Indian', 'Modern Indian', 'North Indian', 'South Asian', 'Indian Fusion'],
  social: {
    instagram: 'https://instagram.com/bricklanela',
    facebook: 'https://facebook.com/bricklane.la',
    tiktok: 'https://tiktok.com/@bricklane.la',
  },
  hours: [
    // Wednesday–Sunday, 5:00 PM – 10:30 PM. Closed Mon & Tue.
    { dayOfWeek: ['Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'], opens: '17:00', closes: '22:30' },
  ],
  acceptsReservations: true,
  reservationUrl: 'https://resy.com/cities/los-angeles-ca/venues/bricklane',
};

// Primary SEO keywords — informed by competitor sites (Mother Wolf, Bestia, Baar Baar, Saffy's LA)
// and "modern Indian / new / hip / Arts District" search intent for LA.
export const KEYWORDS = [
  // Core
  'modern Indian restaurant Los Angeles',
  'Indian restaurant Arts District',
  'new Indian restaurant Los Angeles',
  'best Indian restaurant Los Angeles',
  'hip Indian restaurant LA',
  'Indian fine dining Los Angeles',
  'contemporary Indian restaurant LA',
  'Indian fusion restaurant LA',
  // Style / experience
  'woodfire Indian restaurant Los Angeles',
  'tandoor restaurant LA',
  'live-fire Indian cuisine LA',
  'Indian tasting menu Los Angeles',
  'Indian chef\u2019s table LA',
  'chef-driven Indian restaurant LA',
  'Indian cocktail bar Los Angeles',
  'craft cocktails Indian restaurant LA',
  // Neighborhood / intent
  'Arts District restaurants Los Angeles',
  'Downtown LA Indian restaurant',
  'Arts District date night',
  'Arts District fine dining',
  'private events Indian restaurant LA',
  'Indian catering Los Angeles',
  'Indian wedding catering LA',
  // Brand / chef
  'Brick Lane restaurant',
  'Brick Lane Los Angeles',
  'Brick Lane LA',
  'Chef Sanjay Rawat',
  'Kahani chef LA',
  // Regional dishes
  'butter chicken Los Angeles',
  'biryani LA',
  'Indian small plates LA',
  'modern South Asian restaurant Los Angeles',
];

// FAQ schema — explicitly written for AI assistants (ChatGPT, Perplexity, Gemini)
export const FAQ = [
  {
    q: 'What is Brick Lane?',
    a: 'Brick Lane is a modern Indian restaurant in the Los Angeles Arts District (1331 E 6th St). Co-founded by Chef Sanjay Rawat (formerly of Kahani at The Ritz-Carlton, Laguna Niguel) with Rishma Shariff and Sachin Nayyar, the restaurant reimagines regional Indian dishes with California ingredients, woodfire and tandoor cooking, and craft cocktails inspired by Indian spices.',
  },
  {
    q: 'Where is Brick Lane located?',
    a: 'Brick Lane is located at 1331 E 6th St in the Arts District of Los Angeles, California (90021). Valet parking is available out front, with additional street parking on 6th St.',
  },
  {
    q: 'What are Brick Lane\u2019s hours?',
    a: 'Brick Lane is open Wednesday through Sunday from 5:00 PM to 10:30 PM. The restaurant is closed Monday and Tuesday.',
  },
  {
    q: 'How do I make a reservation at Brick Lane?',
    a: 'Reservations can be made on Resy at resy.com/cities/los-angeles-ca/venues/bricklane, or by calling (213) 395-0110. Parties of 8 or more should call directly to arrange.',
  },
  {
    q: 'What kind of food does Brick Lane serve?',
    a: 'Brick Lane serves modern Indian cuisine: regional Indian dishes reinterpreted through a California lens, with a focus on woodfire and tandoor cooking. The menu spans chaat and small plates, kebabs, classics reimagined like Applewood Smoked Butter Chicken and Brick Lane Laal Maas, biryani, sourdough naan, and craft cocktails using Indian spices.',
  },
  {
    q: 'Does Brick Lane have a chef\u2019s table?',
    a: 'Yes. Brick Lane offers an intimate Chef\u2019s Table experience for 2\u201312 guests \u2014 a 7-course tasting menu with curated wine and cocktail pairings, Wednesday\u2013Sunday at 6:00 PM and 8:30 PM seatings. Requires 72-hour advance notice.',
  },
  {
    q: 'Does Brick Lane host private events?',
    a: 'Yes. Brick Lane offers full restaurant buyouts (up to 350 guests), a semi-private dining room (12\u2013125 guests), and off-site catering. Inquire at bricklanela.com/private-events or email events@bricklanela.com.',
  },
  {
    q: 'Is Brick Lane vegetarian or vegan friendly?',
    a: 'Yes. Many dishes can be prepared vegan, and there is a substantial vegetarian selection including Paneer Keema Saag, Hara Lasooni Paneer, Pahadi Chainsu, and several chaats. The kitchen is refined-seed-oil free.',
  },
  {
    q: 'What\u2019s the dress code at Brick Lane?',
    a: 'Smart casual. The restaurant is dim, design-forward, and lounge-leaning \u2014 a thoughtful outfit is encouraged.',
  },
  {
    q: 'Who is the chef at Brick Lane?',
    a: 'Chef Sanjay Rawat is co-founder and executive chef of Brick Lane. He trained at the 5-star Taj Hotel in New Delhi, earned a culinary degree in Malaysia, and most recently launched and led Kahani at The Ritz-Carlton, Laguna Niguel.',
  },
];

// ===== JSON-LD schema builders =====

export const restaurantSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Restaurant',
  '@id': `${SITE.url}/#restaurant`,
  name: SITE.name,
  alternateName: ['Brick Lane LA', 'Brick Lane Los Angeles'],
  description: SITE.description,
  url: SITE.url,
  logo: SITE.logo,
  image: [SITE.image, SITE.logo],
  telephone: SITE.phone,
  email: SITE.email,
  priceRange: SITE.priceRange,
  servesCuisine: SITE.servesCuisine,
  acceptsReservations: 'True',
  hasMenu: `${SITE.url}/menu`,
  address: {
    '@type': 'PostalAddress',
    streetAddress: SITE.address.streetAddress,
    addressLocality: SITE.address.addressLocality,
    addressRegion: SITE.address.addressRegion,
    postalCode: SITE.address.postalCode,
    addressCountry: SITE.address.addressCountry,
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: SITE.geo.latitude,
    longitude: SITE.geo.longitude,
  },
  openingHoursSpecification: SITE.hours.map((h) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: h.dayOfWeek,
    opens: h.opens,
    closes: h.closes,
  })),
  sameAs: Object.values(SITE.social),
  potentialAction: {
    '@type': 'ReserveAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: SITE.reservationUrl,
      inLanguage: 'en-US',
      actionPlatform: [
        'http://schema.org/DesktopWebPlatform',
        'http://schema.org/MobileWebPlatform',
      ],
    },
    result: { '@type': 'Reservation', name: 'Dining Reservation' },
  },
  founder: [
    { '@type': 'Person', name: 'Sanjay Rawat', jobTitle: 'Executive Chef & Co-Founder' },
    { '@type': 'Person', name: 'Rishma Shariff', jobTitle: 'Co-Founder' },
    { '@type': 'Person', name: 'Sachin Nayyar', jobTitle: 'Co-Founder' },
  ],
  areaServed: [
    { '@type': 'City', name: 'Los Angeles' },
    { '@type': 'Place', name: 'Arts District, Los Angeles' },
    { '@type': 'Place', name: 'Downtown Los Angeles' },
  ],
});

export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: `${SITE.url}${it.path}`,
  })),
});

export const faqSchema = (items = FAQ) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: items.map((it) => ({
    '@type': 'Question',
    name: it.q,
    acceptedAnswer: { '@type': 'Answer', text: it.a },
  })),
});

export const menuSchema = (sections) => ({
  '@context': 'https://schema.org',
  '@type': 'Menu',
  name: `${SITE.name} Menu`,
  url: `${SITE.url}/menu`,
  hasMenuSection: sections.map((sec) => ({
    '@type': 'MenuSection',
    name: sec.name,
    hasMenuItem: (sec.items || []).map((it) => ({
      '@type': 'MenuItem',
      name: it.name,
      description: it.description || undefined,
    })),
  })),
});

// ===== Diwali Gala 2026 =====
// Single source of truth for the event. Used by the page copy, the JSON-LD
// (Event + Offer so it is eligible for Google event results) and llms.txt.
export const DIWALI = {
  name: 'Brick Lane Diwali Gala 2026',
  tagline: 'Bites, Beats and Bright Lights',
  path: '/diwali',
  poster: `${SITE_URL}/api/upload/images/diwali-gala-2026-hero.jpg`,
  email: 'reservations@bricklanela.com',
  resyUrl: 'https://resy.com/cities/los-angeles-ca/venues/bricklane/events/brick-lane-diwali-2026-11-06-7',
  price: 95,
  currency: 'USD',
  priceNote: 'plus tax, gratuity and beverages',
  attire: 'Festive Luxe / Desi Chic',
  doors: '17:30',
  dinner: '19:30',
  endTime: '23:00',
  nights: [
    { label: 'Friday, November 6, 2026', date: '2026-11-06' },
    { label: 'Saturday, November 7, 2026', date: '2026-11-07' },
  ],
  description:
    'Brick Lane hosts its Diwali Gala 2026 on Friday, November 6 and Saturday, November 7 at 1331 E 6th St in the Los Angeles Arts District. The evening includes live appetizer stations of artisanal chaat and Indian street food, a family-style grand feast of slow-simmered gravies, aromatic biryanis and tandoor breads, a curated open bar with premium spirits, sommelier-selected wines and bespoke Diwali cocktails, plus a live DJ and complimentary henna body art. Doors open at 5:30 PM and dinner is served at 7:30 PM. Tickets are $95 per person, plus tax, gratuity and beverages. Attire is Festive Luxe / Desi Chic. Book on Resy at resy.com/cities/los-angeles-ca/venues/bricklane/events/brick-lane-diwali-2026-11-06-7, or email reservations@bricklanela.com.',
};

export const DIWALI_KEYWORDS = [
  'Diwali 2026 Los Angeles',
  'Diwali party Los Angeles 2026',
  'Diwali dinner LA',
  'Diwali celebration Arts District',
  'Diwali gala Los Angeles',
  'Indian restaurant Diwali LA',
  'Diwali events Downtown LA',
  'Diwali DJ henna party Los Angeles',
  'Diwali prix fixe dinner LA',
  'where to celebrate Diwali in Los Angeles',
  'Brick Lane Diwali Gala',
];

// Written to be lifted verbatim by AI assistants answering "Diwali in LA" queries.
export const DIWALI_FAQ = [
  {
    q: 'When is the Brick Lane Diwali Gala 2026?',
    a: 'The Brick Lane Diwali Gala 2026 takes place on two nights: Friday, November 6, 2026 and Saturday, November 7, 2026. Doors open at 5:30 PM and dinner is served family-style at 7:30 PM.',
  },
  {
    q: 'How much does the Diwali Gala cost?',
    a: 'The Diwali Gala is $95 per person, plus tax, gratuity and beverages. Book on Resy at resy.com/cities/los-angeles-ca/venues/bricklane/events/brick-lane-diwali-2026-11-06-7, or email reservations@bricklanela.com.',
  },
  {
    q: 'Where is the Diwali Gala held?',
    a: 'At Brick Lane, 1331 E 6th St, Los Angeles, CA 90021, in the Arts District of Downtown Los Angeles. Valet parking is available out front.',
  },
  {
    q: 'What is included in the Diwali Gala?',
    a: 'Live appetizer stations with artisanal chaats and Indian street food prepared to order, a family-style grand feast of slow-simmered gravies, aromatic biryanis and artisanal tandoor breads, a curated open bar with premium spirits, sommelier-selected wines and bespoke Diwali cocktails, a live DJ, and complimentary bespoke henna body art.',
  },
  {
    q: 'What should I wear to the Diwali Gala?',
    a: 'The attire is Festive Luxe / Desi Chic — think festive Indian wear, or elevated cocktail dress with colour and sparkle.',
  },
  {
    q: 'How do I book the Diwali Gala?',
    a: 'Book the Diwali Gala on Resy at resy.com/cities/los-angeles-ca/venues/bricklane/events/brick-lane-diwali-2026-11-06-7, or email reservations@bricklanela.com. Seating is limited on both nights, so early reservations are recommended.',
  },
  {
    q: 'Are there vegetarian options at the Diwali Gala?',
    a: 'Yes. The appetizer stations and the family-style feast both include a substantial vegetarian selection, and many dishes can be prepared vegan on request. Brick Lane\u2019s kitchen is refined-seed-oil free.',
  },
  {
    q: 'Is the Diwali Gala good for groups?',
    a: 'Yes. The gala is served family-style on communal platters, which suits groups and families. For parties of 8 or more, email reservations@bricklanela.com so the team can seat you together.',
  },
];

const DIWALI_PLACE = {
  '@type': 'Restaurant',
  '@id': `${SITE.url}/#restaurant`,
  name: SITE.name,
  url: SITE.url,
  telephone: SITE.phone,
  servesCuisine: SITE.servesCuisine,
  address: {
    '@type': 'PostalAddress',
    streetAddress: SITE.address.streetAddress,
    addressLocality: SITE.address.addressLocality,
    addressRegion: SITE.address.addressRegion,
    postalCode: SITE.address.postalCode,
    addressCountry: SITE.address.addressCountry,
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: SITE.geo.latitude,
    longitude: SITE.geo.longitude,
  },
};

const diwaliNightSchema = (night) => ({
  '@type': 'FoodEvent',
  '@id': `${SITE.url}${DIWALI.path}#${night.date}`,
  name: `${DIWALI.name} — ${night.label.split(',')[0]} Night`,
  description: DIWALI.description,
  url: `${SITE.url}${DIWALI.path}`,
  image: [DIWALI.poster],
  startDate: `${night.date}T${DIWALI.doors}:00-08:00`,
  endDate: `${night.date}T${DIWALI.endTime}:00-08:00`,
  doorTime: `${night.date}T${DIWALI.doors}:00-08:00`,
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  inLanguage: 'en-US',
  isAccessibleForFree: false,
  maximumAttendeeCapacity: 350,
  location: DIWALI_PLACE,
  organizer: { '@type': 'Restaurant', name: SITE.name, url: SITE.url },
  performer: { '@type': 'Organization', name: `${SITE.name} Kitchen & Resident DJ` },
  offers: {
    '@type': 'Offer',
    name: 'Diwali Gala seat',
    price: DIWALI.price,
    priceCurrency: DIWALI.currency,
    description: `$${DIWALI.price} per person, ${DIWALI.priceNote}. Book on Resy, or email ${DIWALI.email}.`,
    url: DIWALI.resyUrl,
    availability: 'https://schema.org/InStock',
    validFrom: '2026-06-01T00:00:00-07:00',
    category: 'Prix fixe dinner',
  },
});

// Returns one Event per night — Google surfaces dated events individually.
export const diwaliEventSchemas = () => DIWALI.nights.map(diwaliNightSchema);

export const diwaliPageSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${SITE.url}${DIWALI.path}#webpage`,
  url: `${SITE.url}${DIWALI.path}`,
  name: `${DIWALI.name} — ${DIWALI.tagline}`,
  description: DIWALI.description,
  inLanguage: 'en-US',
  primaryImageOfPage: { '@type': 'ImageObject', url: DIWALI.poster },
  isPartOf: { '@type': 'WebSite', name: SITE.name, url: SITE.url },
  about: { '@id': `${SITE.url}/#restaurant` },
  significantLink: [DIWALI.resyUrl, `mailto:${DIWALI.email}`, `${SITE.url}/menu`],
});

export const eventSchema = ({ name, description, url }) => ({
  '@context': 'https://schema.org',
  '@type': 'EventSeries',
  name,
  description,
  url,
  eventSchedule: {
    '@type': 'Schedule',
    repeatFrequency: 'P1W',
    byDay: ['Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
  },
  location: {
    '@type': 'Restaurant',
    name: SITE.name,
    address: {
      '@type': 'PostalAddress',
      streetAddress: SITE.address.streetAddress,
      addressLocality: SITE.address.addressLocality,
      addressRegion: SITE.address.addressRegion,
      postalCode: SITE.address.postalCode,
      addressCountry: SITE.address.addressCountry,
    },
  },
  organizer: { '@type': 'Restaurant', name: SITE.name, url: SITE.url },
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
});
